// Recuperacion cuando un chunk lazy ya no existe.
//
// Escenario: pestaña abierta desde antes del deploy. El usuario aprieta algo
// que dispara un `import()` dinamico (en el admin: cualquiera de los PDFs).
// El chunk que pide tiene el hash viejo en el nombre. Si el deploy borro los
// assets anteriores, ese archivo ya no esta.
//
// Y no falla de forma limpia: CloudFront tiene custom_error_response 403/404
// → /index.html con status 200. O sea que el browser recibe un documento HTML
// con Content-Type text/html donde esperaba un modulo ES. El error resultante
// es un parse error incomprensible, no un 404.
//
// Este modulo detecta esa familia de fallas y recarga. Se instala lo antes
// posible — antes de montar React — porque el error puede ocurrir durante la
// carga inicial de un chunk preloadeado.
//
// La primera linea de defensa igual es no borrar los assets viejos en el
// deploy (ver deploy.mjs); esto es la red por si algo se cae igual.

import { executeReload } from './reload';
import { readJson, writeJson } from './storage';

const KEY = 'authsys:version-guard:chunk-recovery';
/** Mas de esto en la misma pestaña = el reload no arregla nada, no insistas. */
const MAX_RECOVERIES = 2;
const WINDOW_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /expected a javascript(-or-wasm)? module script/i,
  /chunkloaderror/i,
  // El sintoma del index.html servido como modulo: el parser se come el
  // '<' de '<!doctype html>'.
  /unexpected token '<'/i,
];

function looksLikeChunkError(message: string): boolean {
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

interface RecoveryState {
  count: number;
  firstAt: number;
}

function shouldRecover(): boolean {
  const now = Date.now();
  const prev = readJson<RecoveryState>('session', KEY);
  const state: RecoveryState =
    prev && now - prev.firstAt < WINDOW_MS ? prev : { count: 0, firstAt: now };

  if (state.count >= MAX_RECOVERIES) return false;
  writeJson('session', KEY, { count: state.count + 1, firstAt: state.firstAt });
  return true;
}

function messageOf(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'message' in value) {
    return String((value as { message: unknown }).message);
  }
  return '';
}

/**
 * Instala los listeners. Idempotente.
 * @param getBuildId lee el buildId actual (para el estado de la escalera).
 */
export function installChunkErrorRecovery(getBuildId: () => string): void {
  if ((window as { __authsysChunkRecovery?: boolean }).__authsysChunkRecovery) return;
  (window as { __authsysChunkRecovery?: boolean }).__authsysChunkRecovery = true;

  const recover = (reason: string): void => {
    if (!shouldRecover()) return;
    // eslint-disable-next-line no-console
    console.warn(`[version-guard] recargando por chunk inaccesible: ${reason}`);
    const current = getBuildId();
    // target === from: no sabemos el buildId nuevo todavia. La escalera igual
    // detecta el exito comparando contra `from`.
    void executeReload({ from: current, target: current, attempt: 0 });
  };

  // Señal nativa de Vite: la mas precisa, la emite el runtime de preload.
  // Tipada como Event a proposito — asi compila tanto si los tipos de
  // vite/client estan cargados como si no.
  window.addEventListener('vite:preloadError', (ev: Event) => {
    ev.preventDefault();
    recover('vite:preloadError');
  });

  window.addEventListener('error', (ev: ErrorEvent) => {
    const msg = messageOf(ev.error) || ev.message || '';
    if (looksLikeChunkError(msg)) recover(msg);
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const msg = messageOf(ev.reason);
    if (looksLikeChunkError(msg)) recover(msg);
  });
}
